import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import FinanceContainer from "../containers/FinanceContainer";
import { formatCurrency } from "../../../utils";
import {
  RequesttoPayment,
  RequesttoPaymentStatus,
} from "../types/pettyCash.types";
import pettyCashService from "../services/pettyCashService";

type Period = "daily" | "weekly" | "monthly" | "custom";

const STATUS_COLOR: Record<RequesttoPaymentStatus, string> = {
  DRAFT: "#607d8b",
  SUBMITTED: "#1976D2",
  APPROVED: "#2e7d32",
  PREPARED: "#e65100",
  ON_HOLD: "#e65100",
  CANCELLED: "#c62828",
  PAID: "#6a1b9a",
};

const STATUS_LABELS: Record<RequesttoPaymentStatus, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  PREPARED: "Prepared for Processing",
  ON_HOLD: "On Hold",
  CANCELLED: "Cancelled",
  PAID: "Paid",
};

const CHART_COLORS = [
  "#1C4697",
  "#2e7d32",
  "#e65100",
  "#6a1b9a",
  "#c62828",
  "#1565c0",
  "#4e342e",
  "#37474f",
  "#00838f",
  "#ef6c00",
];

function getDateRangeForPeriod(period: Period): {
  fromDate: string;
  toDate: string;
} {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  if (period === "daily") {
    return { fromDate: fmt(today), toDate: fmt(today) };
  }
  if (period === "weekly") {
    const mon = new Date(today);
    mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    return { fromDate: fmt(mon), toDate: fmt(today) };
  }
  if (period === "monthly") {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { fromDate: fmt(first), toDate: fmt(today) };
  }
  return { fromDate: fmt(today), toDate: fmt(today) };
}

const FinanceReportsPage: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const [period, setPeriod] = useState<Period>("monthly");
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState<string>(today);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [cashbookFilter, setCashbookFilter] = useState<string>("");
  const [departmentFilter, setDepartmentFilter] = useState<string>("");
  const [projectFilter, setProjectFilter] = useState<string>("");

  const [requests, setRequests] = useState<RequesttoPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pettyCashService.getList({
        startDate: fromDate,
        endDate: toDate,
        ...(statusFilter !== "all"
          ? { status: statusFilter as RequesttoPaymentStatus }
          : {}),
      });
      setRequests(data);
    } catch {
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Sync date range when period changes (not custom)
  const handlePeriodChange = (_: React.MouseEvent, value: Period | null) => {
    if (!value) return;
    setPeriod(value);
    if (value !== "custom") {
      const { fromDate: f, toDate: t } = getDateRangeForPeriod(value);
      setFromDate(f);
      setToDate(t);
    }
  };

  // Client-side dimension filters
  const filtered = useMemo(() => {
    return requests.filter((r) => {
      if (cashbookFilter && r.cashbook !== cashbookFilter) return false;
      if (departmentFilter && r.department !== departmentFilter) return false;
      if (projectFilter && r.project !== projectFilter) return false;
      return true;
    });
  }, [requests, cashbookFilter, departmentFilter, projectFilter]);

  // Unique dropdown options
  const options = useMemo(() => {
    const cashbooks = new Set<string>();
    const departments = new Set<string>();
    const projects = new Set<string>();
    requests.forEach((r) => {
      if (r.cashbook) cashbooks.add(r.cashbook);
      if (r.department) departments.add(r.department);
      if (r.project) projects.add(r.project);
    });
    return {
      cashbooks: Array.from(cashbooks).sort(),
      departments: Array.from(departments).sort(),
      projects: Array.from(projects).sort(),
    };
  }, [requests]);

  // KPI calculations
  const kpis = useMemo(() => {
    const statuses: RequesttoPaymentStatus[] = [
      "DRAFT",
      "SUBMITTED",
      "APPROVED",
      "PREPARED",
      "ON_HOLD",
      "CANCELLED",
      "PAID",
    ];
    const byStatus: Record<
      RequesttoPaymentStatus,
      { count: number; amount: number }
    > = {} as any;
    statuses.forEach((s) => {
      byStatus[s] = { count: 0, amount: 0 };
    });

    let totalAmount = 0;
    filtered.forEach((r) => {
      totalAmount += r.amount;
      byStatus[r.status].count += 1;
      byStatus[r.status].amount += r.amount;
    });
    return { total: filtered.length, totalAmount, byStatus };
  }, [filtered]);

  // Trend chart — group by date
  const trendData = useMemo(() => {
    const map: Record<string, { date: string; count: number; amount: number }> =
      {};
    filtered.forEach((r) => {
      const d = (r.createdAt ?? "").split("T")[0];
      if (!d) return;
      if (!map[d]) map[d] = { date: d, count: 0, amount: 0 };
      map[d].count += 1;
      map[d].amount += r.amount;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [filtered]);

  // Status pie chart
  const statusPieData = useMemo(() => {
    return (
      [
        "DRAFT",
        "SUBMITTED",
        "APPROVED",
        "PREPARED",
        "ON_HOLD",
        "CANCELLED",
        "PAID",
      ] as RequesttoPaymentStatus[]
    )
      .filter((s) => kpis.byStatus[s].count > 0)
      .map((s) => ({
        name: STATUS_LABELS[s],
        value: kpis.byStatus[s].amount,
        count: kpis.byStatus[s].count,
        color: STATUS_COLOR[s],
      }));
  }, [kpis]);

  // Cashbook horizontal bar chart (top 10)
  const cashbookData = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.forEach((r) => {
      const cb = r.cashbook ?? "Unspecified";
      map[cb] = (map[cb] ?? 0) + r.amount;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filtered]);

  const exportCsv = () => {
    const header = [
      "Ref",
      "Date",
      "Payee",
      "Description",
      "Department",
      "Project",
      "Cashbook",
      "Amount",
      "Payment Type",
      "Status",
    ];
    const rows = filtered.map((r) => [
      r.ref,
      new Date(r.createdAt).toLocaleDateString(),
      r.payee ?? "",
      r.description,
      r.department ?? "",
      r.project ?? "",
      r.cashbook ?? "",
      r.amount.toFixed(2),
      r.paymentType ?? "",
      r.status,
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `r2p-report-${fromDate}-to-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <FinanceContainer>
      {/* Page Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="#12315A">
            Finance Reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Request-to-Payment analytics — configurable period, filters, and
            export.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={loadRequests}
            disabled={loading}
            sx={{ borderColor: "#1C4697", color: "#1C4697", fontWeight: 600 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportCsv}
            disabled={filtered.length === 0 || loading}
            sx={{
              bgcolor: "#1C4697",
              "&:hover": { bgcolor: "#12315A" },
              fontWeight: 600,
            }}
          >
            Export CSV
          </Button>
        </Stack>
      </Box>

      {/* Period Slicer & Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(18,49,90,0.06)",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{
            display: "block",
            mb: 1.5,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          Report Period &amp; Filters
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              flexShrink: 0,
              "& .MuiToggleButton-root.Mui-selected": {
                bgcolor: "#1C4697",
                color: "white",
                "&:hover": { bgcolor: "#12315A" },
              },
            }}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => {
              setPeriod("custom");
              setFromDate(e.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 155 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => {
              setPeriod("custom");
              setToDate(e.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 155 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {(
                [
                  "DRAFT",
                  "SUBMITTED",
                  "APPROVED",
                  "ON_HOLD",
                  "CANCELLED",
                  "PAID",
                ] as RequesttoPaymentStatus[]
              ).map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {options.cashbooks.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Cashbook</InputLabel>
              <Select
                value={cashbookFilter}
                label="Cashbook"
                onChange={(e) => setCashbookFilter(e.target.value)}
              >
                <MenuItem value="">All Cashbooks</MenuItem>
                {options.cashbooks.map((cb) => (
                  <MenuItem key={cb} value={cb}>
                    {cb}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {options.departments.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                label="Department"
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {options.departments.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {options.projects.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectFilter}
                label="Project"
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <MenuItem value="">All Projects</MenuItem>
                {options.projects.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total */}
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              borderRadius: 3,
              borderLeft: "4px solid #12315A",
              boxShadow: "0 4px 16px rgba(18,49,90,0.08)",
              height: "100%",
            }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Total Requests
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#12315A">
                {loading ? "—" : kpis.total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {loading ? "—" : formatCurrency(kpis.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {(
          [
            "SUBMITTED",
            "APPROVED",
            "ON_HOLD",
            "PAID",
            "CANCELLED",
          ] as RequesttoPaymentStatus[]
        ).map((s) => (
          <Grid item xs={12} sm={6} md={2} key={s}>
            <Card
              sx={{
                borderRadius: 3,
                borderLeft: `4px solid ${STATUS_COLOR[s]}`,
                boxShadow: "0 4px 16px rgba(18,49,90,0.08)",
                height: "100%",
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  {STATUS_LABELS[s]}
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color={STATUS_COLOR[s]}
                >
                  {loading ? "—" : kpis.byStatus[s].count}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loading ? "—" : formatCurrency(kpis.byStatus[s].amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Trend Bar Chart */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 16px rgba(18,49,90,0.08)",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="#12315A"
                sx={{ mb: 2 }}
              >
                Payment Requests Over Time
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </Box>
              ) : trendData.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={240}
                >
                  <Typography color="text.secondary">
                    No data for selected period
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={trendData}
                    margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) =>
                        name === "Amount ($)" ? formatCurrency(value) : value
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="count"
                      name="Count"
                      fill="#1C4697"
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="amount"
                      name="Amount ($)"
                      fill="#2e7d32"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Status Pie */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 16px rgba(18,49,90,0.08)",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="#12315A"
                sx={{ mb: 2 }}
              >
                Breakdown by Status
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </Box>
              ) : statusPieData.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={240}
                >
                  <Typography color="text.secondary">No data</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      outerRadius={90}
                      label={({ percent }) =>
                        percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""
                      }
                      labelLine={false}
                    >
                      {statusPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cashbook Horizontal Bar */}
      {!loading && cashbookData.length > 0 && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 16px rgba(18,49,90,0.08)",
            mb: 3,
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="#12315A"
              sx={{ mb: 2 }}
            >
              Spend by Cashbook (Top 10)
            </Typography>
            <ResponsiveContainer
              width="100%"
              height={Math.max(180, cashbookData.length * 36)}
            >
              <BarChart
                data={cashbookData}
                layout="vertical"
                margin={{ top: 4, right: 100, left: 8, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={150}
                />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Bar dataKey="value" name="Amount" radius={[0, 4, 4, 0]}>
                  {cashbookData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={CHART_COLORS[i % CHART_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card
        sx={{ borderRadius: 3, boxShadow: "0 4px 16px rgba(18,49,90,0.08)" }}
      >
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color="#12315A">
            Transaction Detail
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              ({filtered.length} record{filtered.length !== 1 ? "s" : ""})
            </Typography>
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      bgcolor: "#12315A",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <TableCell>Date</TableCell>
                  <TableCell>Ref</TableCell>
                  <TableCell>Payee</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Cashbook</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        No records found for the selected period and filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{ "&:hover": { bgcolor: "rgba(28,70,151,0.04)" } }}
                    >
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        {new Date(r.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: "#1C4697",
                          fontSize: "0.78rem",
                        }}
                      >
                        {r.ref}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        {r.payee ?? "—"}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, fontSize: "0.78rem" }}>
                        <Typography
                          variant="caption"
                          noWrap
                          title={r.description}
                        >
                          {r.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        {r.department ?? "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        {r.project ?? "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        {r.cashbook ?? "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, fontSize: "0.78rem" }}
                      >
                        {formatCurrency(r.amount)}
                      </TableCell>
                      <TableCell>
                        {r.paymentType ? (
                          <Chip
                            label={
                              r.paymentType === "TRANSFER" ? "Transfer" : "Cash"
                            }
                            size="small"
                            sx={{
                              fontSize: "0.68rem",
                              bgcolor:
                                r.paymentType === "TRANSFER"
                                  ? "#e3f2fd"
                                  : "#fce4ec",
                              color:
                                r.paymentType === "TRANSFER"
                                  ? "#1565c0"
                                  : "#c2185b",
                              border: `1px solid ${
                                r.paymentType === "TRANSFER"
                                  ? "#1565c0"
                                  : "#c2185b"
                              }`,
                              fontWeight: 700,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[r.status]}
                          size="small"
                          sx={{
                            fontSize: "0.68rem",
                            bgcolor: `${STATUS_COLOR[r.status]}18`,
                            color: STATUS_COLOR[r.status],
                            fontWeight: 700,
                            border: `1px solid ${STATUS_COLOR[r.status]}40`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </FinanceContainer>
  );
};

export default FinanceReportsPage;
